import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { UserService } from '@services/user.service';
import { environment } from '@env/environment';
import { Router } from '@angular/router';
import { IframeService } from './iframe.service';
import { CookieService } from 'ngx-cookie-service';
import { MainSidebarService } from './main-sidebar.service';
import { PageService } from './page.service';

import {
  ScaffoldDialogService,
  ScaffoldDialogRef,
} from '@app/framework/dialog/scaffold-dialog.service';
import { VerifyPasswordComponent } from '@app/framework/verify-password/verify-password.component';

/** Service for request require authentication to the Scaffold API */
@Injectable({
  providedIn: 'root',
})
export class HttpUtilService {
  dialogRef: ScaffoldDialogRef;
  get timezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private router: Router,
    private iframeService: IframeService,
    private cookieService: CookieService,
    private mainSideBarService: MainSidebarService,
    private pageService: PageService,
    private dialog: ScaffoldDialogService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Send an authenticated request to the Scaffold API
   * @param method Request method
   * @param url URL endpoint for the request
   * @param body Body data for the request
   * @param headers Additional headers to include
   * If you are adding headers to any api calls, you'll have to update the cors config in api gateway to allow for it,
   * add to this list and then
   * Current list of allowed headers :
   * 'Content-Type, X-Amz-Date, Authorization, X-Api-Key, X-Amz-Security-Token, User-Pool-Id, Client-Id'
   * @param authenticated Include authentication information
   * @param rawResponse If the response should be the raw request instead of just the body
   *
   */
  request(
    method: string,
    url: string,
    body?,
    headers?,
    authenticated?: boolean,
    rawResponse?: boolean,
    verifyPassword?: boolean
  ): Observable<any> {
    // if not in iframe and found with lti credentials, logout
    if (
      !this.iframeService.isInIframe &&
      this.cookieService.get('t4ntn033dd')
    ) {
      this.document.cookie = `t4ntn033dd=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      this.mainSideBarService.updateState(false);
      this.pageService.hideMenuUpdates();
      this.userService.logOut().subscribe(e => {
        this.router.navigate(['/login']);
      });
    }
    //all is treated in the backend as if using scaffoldType on the institution anyways
    const activeFeature =
      localStorage.getItem('_scaffold_active_feature') ||'all'
    url +=
      url.indexOf('?') !== -1
        ? `&timezone=${this.timezone}&activeFeature=${encodeURIComponent(activeFeature)}`
        : `?timezone=${this.timezone}&activeFeature=${encodeURIComponent(activeFeature)}`;
    // Check for authenticated call
    // If it is: check user logged in and attach token in headers
    if (authenticated) {
      return this.userService.isLoggedIn().pipe(
        mergeMap(loggedIn => {
          if (loggedIn) {
            const reqHeaders = new HttpHeaders(headers);
            let cleanBody = typeof body === 'string' ? JSON.parse(body) : body;
            if (verifyPassword) {
              return this.openVerifyPasswordDialog().pipe(
                mergeMap(result => {
                  if (result) {
                    cleanBody = { ...cleanBody, ...result };
                    return this.http.request(method, url, {
                      body: cleanBody,
                      headers: reqHeaders,
                      observe: rawResponse ? 'response' : 'body',
                    });
                  }
                  return EMPTY;
                })
              );
            } else {
              return this.http.request(method, url, {
                body: cleanBody,
                headers: reqHeaders,
                observe: rawResponse ? 'response' : 'body',
              });
            }
          } else {
            // Capture current location trying to make request from, user will return after logging in
            // const forwardUrl = this.router.url;
            this.userService.logOut().subscribe(e => {
              this.router.navigate(['/login']);
            });
            //this.router.navigate(['/login'], { queryParams: { 'forwardUrl': forwardUrl } });

            // return of('not logged in')
          }
        })
      );
    } else {
      return this.http.request(method, url, {
        body: body,
        headers: new HttpHeaders(headers),
        observe: rawResponse ? 'response' : 'body',
      });
    }
  }
  /**
   *  Get the headers for authenticated requests
   * @param headers Additional headers to include
   */
  private getHeaders(headers) {
    let auth = {
      'Content-Type': 'application/json',
      'X-Amz-Date': new Date().toISOString(),
      'User-Pool-Id': environment.cognitoUserPoolId,
      'Client-Id': environment.cognitoClientId,
      Authorization: 'Bearer ' + this.userService.accessToken,
    };
    headers = Object.assign(auth, headers);
    return headers;
  }

  /**
   * Quick and easy wrapper for authenticated get calls
   * @param url URL endpoint for the request
   * @param headers Additional headers to include
   * If you are adding headers to any api calls, you'll have to update the cors config in api gateway to allow for it,
   * add to this list and then
   * Current list of allowed headers :
   * 'Content-Type, X-Amz-Date, Authorization, X-Api-Key, X-Amz-Security-Token, User-Pool-Id, Client-Id'
   * @param rawResponse If the response should be the raw request instead of just the body
   */
  authGet(url, headers?, rawResponse?) {
    // if not in iframe and found with lti credentials, logout
    if (
      !this.iframeService.isInIframe &&
      this.cookieService.get('t4ntn033dd')
    ) {
      this.document.cookie = `t4ntn033dd=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      this.userService.logOut().subscribe(e => {
        this.mainSideBarService.updateState(false);
        this.pageService.hideMenuUpdates();
        this.router.navigate(['/login']);
      });
    }
    return this.request('GET', url, null, headers, true, rawResponse);
  }

  openVerifyPasswordDialog(): Observable<any> {
    // this.movingItem.setPropertiesItem(null);
    let settings: any = {
      context: {
        data: {
          title: 'Verify Password',
        },
        closeOnBackdropClick: false,
        closeOnEsc: false,
      },
    };
    this.dialogRef = this.dialog.open(VerifyPasswordComponent, settings);

    return this.dialogRef.onClose;
  }
}
